import React, {useCallback, useEffect, useMemo, useState} from 'react'
import { Button, Icon, Placeholder } from 'semantic-ui-react'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { APFrameDetailsType } from './ap.types'
import { addToCart, removeFromCart, selectCartItems } from './apCart.slice'
import { clearSelectFrame } from './apFrames.slice'
import PreviewImage from './PreviewImage'
import {AP_IMAGE_PATH, RECAPTCHA_ENABLED} from "../../environment";
import {generateRecaptchaToken} from "../../common/recapctcha";
import {addRecaptchaToURL} from "../../common/util";
import {APUtils} from "./ap.util";

type PhotoAttributeRowProps = {
  label: string
  value: string | number
}

const PhotoAttributeRow = ({ label, value }: PhotoAttributeRowProps) => {
  return (
    <tr>
      <th style={{textAlign: "left"}}>
        {label}:
      </th>
      <td style={{padding: 0, margin: 0}}>
        {value}
      </td>
    </tr>
  )
}

type APFrameDetailsPanelProps = {
  selectedFrameDetails: APFrameDetailsType
}

const APFrameDetailsPanel = ({ selectedFrameDetails }: APFrameDetailsPanelProps) => {
  const dispatch = useAppDispatch()

  const cartFrames = useAppSelector(selectCartItems)
  const [ recaptchaV3Key, setRecaptchaV3Key] = useState<string>("")

  useEffect( () => {
    if (RECAPTCHA_ENABLED) {
      generateRecaptchaToken().then((res) => setRecaptchaV3Key(res));
    }
  }, [])

  const width = 260
  const height = 260
  const quality = 100

  const LoadingElement = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: width, height: height }}>
        <Placeholder style={{ width: width, height: height }}>
          <Placeholder.Image/>
        </Placeholder>
      </div>
    )
  }

  const ErrorElement = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: width, height: height }}>
        <div>
          <h4 style={{ textAlign: 'center' }}>No digitised image available</h4>
        </div>
      </div>
    )
  }

  const isInCart = useMemo(() => {
    return cartFrames.find(cartItem => cartItem.frame.id === selectedFrameDetails.id) !== undefined
  }, [cartFrames, selectedFrameDetails.id])

  const handleAddToCart = useCallback(() => {
    dispatch(addToCart(selectedFrameDetails))
  }, [dispatch, selectedFrameDetails])

  const handleRemoveFromCart = useCallback(() => {
    dispatch(removeFromCart(selectedFrameDetails))
  }, [dispatch, selectedFrameDetails])

  const handleUnselectFrame = () => {
    dispatch(clearSelectFrame())
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} key={selectedFrameDetails.id}>
      <div style={{display: 'flex', flexDirection: 'row', flexGrow: 1, minWidth: '200px'}}>
        <Icon corner={'top left'} name={'close'} size={'small'} link onClick={handleUnselectFrame}/>
        <table style={{}}>
          <tbody>
          <PhotoAttributeRow label={'Project'} value={selectedFrameDetails.project}/>
          <PhotoAttributeRow label={'Date'} value={selectedFrameDetails.date}/>
          <PhotoAttributeRow label={'Film'} value={selectedFrameDetails.film_type + ' ' + selectedFrameDetails.film}/>
          <PhotoAttributeRow label={'Run'} value={selectedFrameDetails.run}/>
          <PhotoAttributeRow label={'Frame'} value={selectedFrameDetails.frame}/>
          <PhotoAttributeRow label={'Type'} value={selectedFrameDetails.camera}/>
          {selectedFrameDetails.camera === 'Film' &&
              <PhotoAttributeRow label={'Scale'} value={'1:' + selectedFrameDetails.scale}/>}
          {selectedFrameDetails.camera === 'Digital' &&
              <PhotoAttributeRow label={'Resolution'} value={selectedFrameDetails.scale + 'cm'}/>}
          </tbody>
        </table>
        <div style={{flexGrow: 1, display: 'flex', justifyContent: 'center'}}>
          { ( (RECAPTCHA_ENABLED && recaptchaV3Key.length > 0) || !RECAPTCHA_ENABLED) &&
          <PreviewImage
              key={APUtils.imgSrcUrl({selectedFrameDetails: selectedFrameDetails, width: width, height: height, quality: quality, recaptchaToken: recaptchaV3Key})}
              src={APUtils.imgSrcUrl({selectedFrameDetails: selectedFrameDetails, width: width, height: height, quality: quality, recaptchaToken: recaptchaV3Key})}
              loadingElement={<LoadingElement/>}
              errorElement={<ErrorElement/>}
          />
          }
        </div>
      </div>
      <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
        {!isInCart && <Button onClick={handleAddToCart} primary={true}>Add to Cart</Button>}
        {isInCart && <Button onClick={handleRemoveFromCart}>Remove from Cart</Button>}
    </div>


    </div>
  )
}

export default APFrameDetailsPanel